import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FiPlus, FiArchive, FiLoader, FiCheckCircle, FiXCircle,
  FiUsers, FiPercent, FiClock, FiLink, FiCopy, FiExternalLink,
} from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';

/**
 * Growth Plan §2.2 — Founding Member batches admin.
 *
 * Workflow the admin follows:
 *   1. In Stripe Dashboard → Products → Coupons, create a coupon like
 *      "FOUNDING40" at 40% off forever, "Limit total redemptions" = 30.
 *   2. Copy the coupon ID (e.g. `FOUNDING40`).
 *   3. Open this panel → New batch → paste the coupon ID + seat cap.
 *   4. Share https://snap-chart.ai/founding with prospects — they see
 *      seats remaining, click Claim, and land in Stripe checkout with
 *      the promo pre-applied.
 *
 * The seat cap is enforced on our side (fast) AND on Stripe's side
 * (redemption cap) so a race can never over-sell.
 */

interface Batch {
  id: string;
  name: string;
  stripe_coupon_id: string;
  discount_pct: number;
  seats_total: number;
  seats_taken: number;
  seats_remaining: number;
  valid_until: string | null;
  active: boolean;
  created_at: string;
}

const FoundingMembersPanel: React.FC = () => {
  const [batches, setBatches] = useState<Batch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: 'Founding 30 · 40% off for life',
    stripe_coupon_id: 'FOUNDING40',
    discount_pct: 40,
    seats_total: 30,
    valid_until: '',
  });
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.rpc('admin_list_founding_batches');
    if ((data as any)?.success) setBatches(((data as any).batches || []) as Batch[]);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const create = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const { data, error } = await supabase.rpc('admin_create_founding_batch', {
        p_name:             form.name.trim(),
        p_stripe_coupon_id: form.stripe_coupon_id.trim(),
        p_discount_pct:     form.discount_pct,
        p_seats_total:      form.seats_total,
        p_valid_until:      form.valid_until ? new Date(form.valid_until).toISOString() : null,
      });
      if (error) throw error;
      if (!(data as any)?.success) throw new Error((data as any)?.error || 'Failed');
      setMsg({ ok: true, text: 'Batch created.' });
      setShowNew(false);
      load();
    } catch (err) {
      setMsg({ ok: false, text: (err as Error).message });
    } finally {
      setSaving(false);
    }
  };

  const archive = async (id: string) => {
    if (!confirm('Archive this batch? Users will no longer see it as an active offer.')) return;
    await supabase.rpc('admin_archive_founding_batch', { p_id: id });
    load();
  };

  const copyLink = () => {
    navigator.clipboard.writeText('https://snap-chart.ai/founding');
    setMsg({ ok: true, text: 'Public link copied to clipboard.' });
  };

  return (
    <div className="max-w-5xl mx-auto">
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan-400 mb-2">
            ▸ Founding Members
          </div>
          <h2 className="text-2xl font-black text-white">Scarcity-driven conversion.</h2>
          <p className="text-sm text-gray-500 mt-1 max-w-2xl">
            Create a limited-seat, forever-discount offer for your earliest paying users. Public page: <code className="text-cyan-400">/founding</code>.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={copyLink}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-white/10 text-gray-200 hover:bg-white/5 text-xs"
          >
            <FiCopy className="w-3.5 h-3.5" />
            Copy public link
          </button>
          <button
            onClick={() => setShowNew(v => !v)}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold hover:scale-[1.02] transition-transform"
          >
            <FiPlus className="w-4 h-4" />
            New batch
          </button>
        </div>
      </div>

      {msg && (
        <div className={`mb-4 px-4 py-2.5 rounded-lg border font-mono text-xs ${
          msg.ok
            ? 'bg-green-500/5 border-green-500/30 text-green-300'
            : 'bg-red-500/5 border-red-500/30 text-red-300'
        }`}>
          {msg.ok ? '✓' : '✗'} {msg.text}
        </div>
      )}

      {/* Workflow reminder */}
      <div className="mb-6 rounded-2xl border border-cyan-500/20 bg-cyan-500/5 p-4">
        <div className="font-mono text-[10px] uppercase tracking-widest text-cyan-400 mb-2">
          ▸ How to launch a batch
        </div>
        <ol className="text-xs text-gray-300 space-y-1 list-decimal list-inside">
          <li>Stripe Dashboard → Products → <a className="text-cyan-400 underline" href="https://dashboard.stripe.com/coupons" target="_blank" rel="noreferrer">Coupons <FiExternalLink className="w-3 h-3 inline" /></a> — create <span className="text-white">e.g. FOUNDING40 (40% off forever, limit 30 redemptions)</span>.</li>
          <li>Copy the coupon ID.</li>
          <li>Click <span className="text-cyan-400">New batch</span> below and paste it with the same seat cap.</li>
          <li>Share <span className="text-cyan-400">snap-chart.ai/founding</span> with your Hot users.</li>
        </ol>
      </div>

      {/* New batch form */}
      {showNew && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6 rounded-2xl border border-white/10 bg-black/40 p-5 space-y-4"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                Name (admin only)
              </label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400/50"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                Stripe coupon ID
              </label>
              <input
                type="text"
                value={form.stripe_coupon_id}
                onChange={e => setForm({ ...form, stripe_coupon_id: e.target.value })}
                className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-cyan-400/50"
                placeholder="FOUNDING40"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                Discount % (display)
              </label>
              <input
                type="number"
                min={1}
                max={100}
                value={form.discount_pct}
                onChange={e => setForm({ ...form, discount_pct: parseInt(e.target.value) || 0 })}
                className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400/50"
              />
            </div>
            <div>
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                Seats total
              </label>
              <input
                type="number"
                min={1}
                value={form.seats_total}
                onChange={e => setForm({ ...form, seats_total: parseInt(e.target.value) || 0 })}
                className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400/50"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-1.5">
                Valid until (optional — auto-closes)
              </label>
              <input
                type="datetime-local"
                value={form.valid_until}
                onChange={e => setForm({ ...form, valid_until: e.target.value })}
                className="w-full bg-black/60 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-cyan-400/50"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setShowNew(false)}
              className="px-4 py-2 rounded-lg border border-white/10 text-gray-300 text-sm hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              onClick={create}
              disabled={saving || !form.name || !form.stripe_coupon_id || form.seats_total < 1}
              className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-gradient-to-r from-cyan-400 to-purple-400 text-black text-sm font-bold hover:scale-[1.02] transition-transform disabled:opacity-40"
            >
              {saving ? <FiLoader className="w-4 h-4 animate-spin" /> : <FiPlus className="w-4 h-4" />}
              Create batch
            </button>
          </div>
        </motion.div>
      )}

      {/* Batches list */}
      {loading ? (
        <div className="text-center py-12 text-gray-500 font-mono text-xs">Loading…</div>
      ) : batches.length === 0 ? (
        <div className="text-center py-14 border border-dashed border-white/10 rounded-2xl">
          <FiUsers className="w-8 h-8 text-gray-600 mx-auto mb-3" />
          <p className="text-white font-medium mb-1">No founding batches yet</p>
          <p className="text-xs text-gray-500 font-mono">Create one to unlock the /founding public page.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {batches.map(b => {
            const pctFilled = (b.seats_taken / b.seats_total) * 100;
            const isExpired = b.valid_until && new Date(b.valid_until) < new Date();
            const isFull = b.seats_remaining <= 0;
            return (
              <div key={b.id} className="rounded-2xl border border-white/10 bg-black/40 p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1.5">
                      <h3 className="text-white font-bold truncate">{b.name}</h3>
                      {b.active && !isExpired && !isFull && (
                        <span className="px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/30 text-green-400 text-[10px] font-mono uppercase tracking-widest">
                          ● Active
                        </span>
                      )}
                      {isFull && (
                        <span className="px-2 py-0.5 rounded-full bg-orange-500/10 border border-orange-500/30 text-orange-400 text-[10px] font-mono uppercase tracking-widest">
                          Full
                        </span>
                      )}
                      {isExpired && (
                        <span className="px-2 py-0.5 rounded-full bg-red-500/10 border border-red-500/30 text-red-400 text-[10px] font-mono uppercase tracking-widest">
                          Expired
                        </span>
                      )}
                      {!b.active && (
                        <span className="px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-gray-500 text-[10px] font-mono uppercase tracking-widest">
                          Archived
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div>
                        <div className="text-gray-500 font-mono uppercase text-[10px] tracking-widest">Coupon</div>
                        <div className="text-white font-mono">{b.stripe_coupon_id}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 font-mono uppercase text-[10px] tracking-widest">Discount</div>
                        <div className="text-white font-mono">{b.discount_pct}%</div>
                      </div>
                      <div>
                        <div className="text-gray-500 font-mono uppercase text-[10px] tracking-widest">Seats</div>
                        <div className="text-white font-mono">{b.seats_taken} / {b.seats_total}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 font-mono uppercase text-[10px] tracking-widest">Valid until</div>
                        <div className="text-white font-mono">
                          {b.valid_until ? new Date(b.valid_until).toLocaleDateString() : '—'}
                        </div>
                      </div>
                    </div>
                    {/* Progress bar */}
                    <div className="mt-3 h-1.5 rounded-full bg-white/5 overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          pctFilled >= 90 ? 'bg-orange-400' : pctFilled >= 50 ? 'bg-cyan-400' : 'bg-green-400'
                        }`}
                        style={{ width: `${Math.min(100, pctFilled)}%` }}
                      />
                    </div>
                  </div>
                  {b.active && (
                    <button
                      onClick={() => archive(b.id)}
                      className="p-2 rounded-lg border border-white/10 text-gray-500 hover:text-white hover:border-red-500/40 transition-colors"
                      title="Archive"
                    >
                      <FiArchive className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FoundingMembersPanel;
